import {
  GoogleSocialAuthError,
  type GoogleSocialAuthErrorCode,
  type GoogleSocialAuthStage,
  type SocialProvider,
} from "@/lib/social-auth";
import { reportWebError } from "@/observability/report-error";
import { getApiError, isApiClientError } from "@membentry/shared-api";
import type { ReportabilityKind } from "@membentry/shared-observability";

const normalizeCode = (error: unknown): GoogleSocialAuthErrorCode | string =>
  error instanceof GoogleSocialAuthError ? error.code : getApiError(error)?.code || "unknown";
const getHttpStatus = (error: unknown): number | undefined =>
  error instanceof GoogleSocialAuthError ? error.httpStatus :
    isApiClientError(error) ? error.status : undefined;
const getKind = (code: string, status?: number): ReportabilityKind => {
  if (code === "cancelled") return "expected-user-action";
  if (code === "account_conflict" || typeof status === "number") return "api-owned";
  if (code.startsWith("transaction_") || code === "state_mismatch") return "operational";
  return "unexpected-runtime";
};

/** Reports bounded metadata only; credentials, state, tokens and PII are never accepted. */
export const reportSocialAuthFailure = (
  stage: GoogleSocialAuthStage,
  error: unknown,
  provider?: SocialProvider,
): string | undefined => {
  const normalizedCode = normalizeCode(error);
  const httpStatus = getHttpStatus(error);
  const kind = getKind(normalizedCode, httpStatus);
  const resolvedProvider = provider ??
    (error instanceof GoogleSocialAuthError ? error.provider : "google");
  if (
    kind === "expected-user-action" ||
    (kind === "api-owned" && stage !== "session")
  ) return undefined;

  const displayProvider = resolvedProvider === "apple" ? "Apple" : "Google";
  return reportWebError({
    error: new Error(`${displayProvider} social auth failed${normalizedCode ? `: ${normalizedCode}` : ""}`),
    context: `${displayProvider} social authentication failed`,
    kind,
    httpStatus,
    tags: {
      provider: resolvedProvider,
      surface: "web",
      "auth.stage": stage,
      "auth.error_code": normalizedCode,
      "http.status_code": httpStatus?.toString(),
    },
    fingerprint: ["web", "auth", `social-${resolvedProvider}`, stage, normalizedCode],
  });
};
